import { QueryCache, QueryClient } from "@tanstack/react-query";

import { isPortalAuthError } from "../api/portal-client.js";

/**
 * Global auth-expiry safety net (assessment U1).
 *
 * The portal README promises "the SPA's TanStack Query error handler
 * routes a 401 to /portal/sign-in" — this is where that promise is
 * kept. Any portal query that fails with 401/403 (session cookie
 * missing, expired, or de-authorised) routes the client to sign-in
 * once, globally, instead of each page rendering a Retry that can
 * never succeed.
 *
 * The router doesn't exist yet at module-eval time (main.tsx creates
 * it after importing this client), so `main.tsx` registers the
 * navigation via `setAuthExpiredHandler` once the router is live —
 * that keeps the redirect an SPA navigation rather than a full reload.
 */
let onAuthExpired: (() => void) | null = null;

export function setAuthExpiredHandler(handler: () => void): void {
  onAuthExpired = handler;
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      if (isPortalAuthError(error)) {
        onAuthExpired?.();
      }
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // Never retry an expired session (the redirect handles it); a
      // transient failure still gets one retry.
      retry: (failureCount, error) => !isPortalAuthError(error) && failureCount < 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});
